"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref, update, push, remove, onValue } from "firebase/database"
import { onAuthStateChanged, getAuth } from "firebase/auth"
import { db } from "../Firebase/firestore"
import { set } from "firebase/database"
import "./userQueries.css"
import InfoIcon from "./infoIcon"
import {
  AlertCircle,
  Trash2,
  Clock,
  CheckCircle,
  Send,
  Package,
  Truck,
  Wrench,
  Building2,
  Calendar,
  MessageSquare,
  Filter,
} from "lucide-react"

// Function to get status icon
function getStatusIcon(status) {
  switch (status) {
    case "Pending":
      return <Clock className="status-icon pending" size={16} />
    case "In Progress":
      return <Send className="status-icon in-progress" size={16} />
    case "Resolved":
      return <CheckCircle className="status-icon resolved" size={16} />
    default:
      return <AlertCircle className="status-icon error" size={16} />
  }
}

const UserQueries = ({ adminView = false, selectedBrand = "" }) => {
  const [queries, setQueries] = useState([])
  const [submittedQueries, setSubmittedQueries] = useState([])
  const [filteredQueries, setFilteredQueries] = useState([])
  const [filteredSubmittedQueries, setFilteredSubmittedQueries] = useState([])
  const [supplierReplies, setSupplierReplies] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [selectedCompany, setSelectedCompany] = useState("")
  const [companies, setCompanies] = useState([])
  const [companiesWithStats, setCompaniesWithStats] = useState([])
  const [brand, setBrand] = useState(adminView ? selectedBrand : localStorage.getItem("supplierBrand"))
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [selectedQueryId, setSelectedQueryId] = useState(null)
  const authInstance = getAuth()
  const gifUrl = "/Images/New notification.gif"
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [selectedRespondedQueryId, setSelectedRespondedQueryId] = useState(null)

  // Maximum response length limit
  const MAX_RESPONSE_LENGTH = 500

  // Update brand when selectedBrand prop changes (for admin view)
  useEffect(() => {
    if (adminView) {
      setBrand(selectedBrand)
    }
  }, [adminView, selectedBrand])

  const sendNotification = async (userId, title, message) => {
    const notificationRef = ref(db, `user_notifications/Query Response/${userId}`)
    const newNotificationRef = push(notificationRef)
    const newNotification = {
      title,
      message,
      gifUrl,
      timestamp: new Date().toISOString(),
      seen: false,
    }
    try {
      await set(newNotificationRef, newNotification)
      console.log("Notification sent successfully")
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }

  useEffect(() => {
    const checkAuthState = () => {
      setIsLoading(true)
      setError("")
      if (adminView) {
        fetchQueries(brand)
        fetchSubmittedQueries(brand)
      } else {
        const unsubscribe = onAuthStateChanged(authInstance, async (user) => {
          if (user) {
            if (!brand) {
              setError("Brand not selected. Please log in again.")
              setIsLoading(false)
            } else {
              fetchQueries(brand)
              fetchSubmittedQueries(brand)
            }
          } else {
            setError("User not authenticated. Please log in again.")
            setIsLoading(false)
          }
        })
        return () => unsubscribe()
      }
    }
    checkAuthState()
  }, [brand, adminView, selectedCategory]) // Removed selectedCompany from dependencies

  const fetchQueries = (selectedBrand) => {
    try {
      const queriesRef = ref(db, "queries")
      onValue(
        queriesRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val()
            const filteredQueries = Object.entries(data).flatMap(([userId, userQueries]) =>
              Object.entries(userQueries)
                .filter(([, query]) => {
                  const hasNoReply = !query.supplierReply || Object.keys(query.supplierReply).length === 0
                  const categoryMatch =
                    selectedCategory === "all" ||
                    (query.category && query.category.toLowerCase() === selectedCategory.toLowerCase())

                  let matchesBrand = false
                  if (query.category === "Cement" ||  !query.category) {
                    matchesBrand = adminView && !selectedBrand ? true : query.brand === selectedBrand
                  } else if (query.category === "Steel") {
                    matchesBrand = adminView ? true : query.steelType === selectedBrand
                  } else if (query.category === "Machinery") {
                    matchesBrand = adminView ? true : query.machineryType === selectedBrand
                  } else if (query.category === "Finance") {
                    matchesBrand = adminView ? true : query.financeType === selectedBrand
                  }

                  return hasNoReply && matchesBrand && categoryMatch && query.companyName
                })
                .map(([queryId, query]) => ({
                  userId,
                  queryId,
                  ...query,
                  displayTimestamp: query.timestamp
                    ? new Date(query.timestamp).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
                    : new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
                })),
            )

            // Sort by timestamp - newest first
            filteredQueries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            setQueries(filteredQueries)
            setFilteredQueries(filteredQueries)
            setError("")
            setIsLoading(false)
          } else {
            setQueries([])
            setFilteredQueries([])
            if (!adminView || (adminView && selectedBrand)) {
              setError("No queries found for this brand.")
            }
            setIsLoading(false)
          }
        },
        (error) => {
          setError("Error fetching queries: " + error.message)
          setIsLoading(false)
        },
      )
    } catch (err) {
      setError("Error fetching queries: " + err.message)
      setIsLoading(false)
    }
  }

  const fetchSubmittedQueries = (selectedBrand) => {
    try {
      const queriesRef = ref(db, "queries")
      onValue(
        queriesRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val()
            const submitted = Object.entries(data).flatMap(([userId, userQueries]) =>
              Object.entries(userQueries)
                .filter(([, query]) => {
                  const hasReply = query.supplierReply && Object.keys(query.supplierReply).length > 0
                  const categoryMatch =
                    selectedCategory === "all" ||
                    (query.category && query.category.toLowerCase() === selectedCategory.toLowerCase())

                  let matchesBrand = false
                  if (query.category === "Cement" ||  !query.category) {
                    matchesBrand = adminView && !selectedBrand ? true : query.brand === selectedBrand
                  } else if (query.category === "Steel") {
                    matchesBrand = adminView ? true : query.steelType === selectedBrand
                  } else if (query.category === "Machinery") {
                    matchesBrand = adminView ? true : query.machineryType === selectedBrand
                  } else if (query.category === "Finance") {
                    matchesBrand = adminView ? true : query.financeType === selectedBrand
                  }

                  return hasReply && matchesBrand && categoryMatch && query.companyName
                })
                .map(([queryId, query]) => ({ userId, queryId, ...query })),
            )

            // Sort by submission date - newest first
            submitted.sort((a, b) => {
              const dateA = new Date(a.supplierReply.submittedAt)
              const dateB = new Date(b.supplierReply.submittedAt)
              return dateB - dateA
            })

            setSubmittedQueries(submitted)
            setFilteredSubmittedQueries(submitted)
          } else {
            setSubmittedQueries([])
            setFilteredSubmittedQueries([])
          }
        },
        (error) => {
          console.error("Error fetching submitted queries:", error)
        },
      )
    } catch (err) {
      console.error("Error fetching submitted queries:", err)
    }
  }

  const updateCompaniesWithStats = (newQueries, submittedQueries) => {
    const allQueries = [...newQueries, ...submittedQueries]
    const companyStats = {}

    allQueries.forEach((query) => {
      const companyName = query.companyName || "Unknown"
      if (!companyStats[companyName]) {
        companyStats[companyName] = {
          name: companyName,
          newCount: 0,
          respondedCount: 0,
          lastQueryDate: null,
        }
      }

      if (query.supplierReply) {
        companyStats[companyName].respondedCount++
      } else {
        companyStats[companyName].newCount++
      }

      const queryDate = new Date(query.timestamp)
      if (!companyStats[companyName].lastQueryDate || queryDate > companyStats[companyName].lastQueryDate) {
        companyStats[companyName].lastQueryDate = queryDate
      }
    })

    const companiesArray = Object.values(companyStats).sort((a, b) => {
      if (a.newCount > 0 && b.newCount === 0) return -1
      if (a.newCount === 0 && b.newCount > 0) return 1
      return b.lastQueryDate - a.lastQueryDate
    })

    setCompaniesWithStats(companiesArray)
    setCompanies(companiesArray.map((c) => c.name))
  }

  // Update companies stats whenever data changes
  useEffect(() => {
    updateCompaniesWithStats(queries, submittedQueries)
  }, [queries, submittedQueries])

  // Filter queries based on selected company
  useEffect(() => {
    if (selectedCompany) {
      setFilteredQueries(queries.filter((q) => q.companyName === selectedCompany))
      setFilteredSubmittedQueries(submittedQueries.filter((q) => q.companyName === selectedCompany))
    } else {
      setFilteredQueries(queries)
      setFilteredSubmittedQueries(submittedQueries)
    }
  }, [selectedCompany, queries, submittedQueries])

  const handleInputChange = (queryId, field, value) => {
    setSupplierReplies((prev) => ({
      ...prev,
      [queryId]: {
        ...prev[queryId],
        [field]: value,
      },
    }))
  }

  const handleSendReply = async (query) => {
    const reply = supplierReplies[query.queryId] || {}
    if (!reply.responseMessage) {
      setError("Please provide a response message.")
      return
    }

    if (reply.responseMessage.length > MAX_RESPONSE_LENGTH) {
      setError(`Response message is too long. Maximum ${MAX_RESPONSE_LENGTH} characters allowed.`)
      return
    }

    try {
      const supplierReply = {
        supplierEmail: localStorage.getItem("supplierEmail") || "admin@altrange.com",
        responseMessage: reply.responseMessage,
        submittedAt: new Date().toISOString(),
        seen: true,
      }

      const replyRef = ref(db, `queries/${query.userId}/${query.queryId}/supplierReply`)
      await update(replyRef, supplierReply)

      setSupplierReplies((prev) => ({
        ...prev,
        [query.queryId]: {},
      }))

      setSelectedQueryId(null)
      setSuccessMessage("Reply sent successfully!")
      setTimeout(() => setSuccessMessage(""), 3000)

      const notificationMessage = `Your query response has been sent for site address: ${query.siteAddress}`
      await sendNotification(query.userId, "Query Response", notificationMessage)
    } catch (err) {
      console.error("Error sending reply:", err)
      setError("Failed to send the reply. Please try again.")
    }
  }

  const handleDeleteQuery = async (query) => {
    if (!query.supplierReply) {
      return
    }

    if (deleteConfirm === query.queryId) {
      try {
        const db = getDatabase()
        await remove(ref(db, `queries/${query.userId}/${query.queryId}`))
        setSuccessMessage("Query deleted successfully")
        setTimeout(() => setSuccessMessage(""), 3000)
        setDeleteConfirm(null)
      } catch (error) {
        console.error("Error deleting query:", error)
        setError("Failed to delete the query. Please try again.")
      }
    } else {
      setDeleteConfirm(query.queryId)
      setTimeout(() => setDeleteConfirm(null), 3000)
    }
  }

  const getCategoryIcon = (category) => {
    if (!category || category === "Cement") {
      return <Package size={16} />
    } else if (category === "Steel") {
      return <Package size={16} />
    } else if (category === "Machinery") {
      return <Wrench size={16} />
    }
    return <Package size={16} />
  }

  if (isLoading)
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading queries...</p>
      </div>
    )

  return (
    <div className="queries-container">
      {/* Header */}
      <div className="queries-header">
        <h1>
          User Queries 
          <InfoIcon text="This section allows you to manage user queries related to your company. View questions from customers regarding your products, services, or order details. You can filter queries by company name and respond directly to users, ensuring smooth communication and improved customer satisfaction." />
        </h1>
        {/* Category Filter - Only for admin */}
        {adminView && (
          <div className="category-filter">
            <Filter size={16} />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="category-select"
            >
              <option value="all">All Categories</option>
              <option value="cement">Cement</option>
              <option value="steel">Steel</option>
              <option value="machinery">Machinery</option>
              <option value="finance">Finance</option>
            </select>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Three Column Layout */}
      <div className="three-column-layout">
        {/* Left Column - Company Filter */}
        <div className="left-column">
          <div className="column-header">
            <Building2 size={18} />
            <h3>Companies</h3>
          </div>
          <div className="company-filter-list">
            <div
              className={`company-filter-item ${selectedCompany === "" ? "active" : ""}`}
              onClick={() => setSelectedCompany("")}
            >
              <div className="company-info">
                <span className="company-name">All Companies</span>
                <div className="company-stats">
                  <span className="new-count">{queries.length} new</span>
                  <span className="responded-count">{submittedQueries.length} responded</span>
                </div>
              </div>
            </div>
            {companiesWithStats.map((company, index) => (
              <div
                key={index}
                className={`company-filter-item ${selectedCompany === company.name ? "active" : ""}`}
                onClick={() => setSelectedCompany(company.name)}
              >
                <div className="company-info">
                  <span className="company-name">{company.name}</span>
                  <div className="company-stats">
                    {company.newCount > 0 && <span className="new-count">{company.newCount} new</span>}
                    {company.respondedCount > 0 && (
                      <span className="responded-count">{company.respondedCount} responded</span>
                    )}
                  </div>
                  {company.lastQueryDate && (
                    <div className="last-query-date">
                      <Calendar size={12} />
                      <span>{company.lastQueryDate.toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                {company.newCount > 0 && <div className="new-indicator"></div>}
              </div>
            ))}
          </div>
        </div>

        {/* Center Column - New Queries */}
        <div className="center-column">
          <div className="column-header">
            <MessageSquare size={18} />
            <h3>New Queries</h3>
            <span className="count-badge">{filteredQueries.length}</span>
          </div>
          <div className="queries-list">
            {filteredQueries.length > 0 ? (
              filteredQueries.map((query) => {
                const category = query.category?.toLowerCase() || "cement"
                const isSelected = selectedQueryId === query.queryId
                return (
                  <div
                    key={query.queryId}
                    className={`query-card new-query ${isSelected ? "selected" : ""}`}
                    onClick={(e) => {
                      // Only expand/collapse if the click isn't on a child interactive element
                      if (
                        e.target.tagName !== "TEXTAREA" &&
                        e.target.tagName !== "BUTTON" &&
                        !e.target.closest(".response-textarea") &&
                        !e.target.closest(".submit-button")
                      ) {
                        setSelectedQueryId(isSelected ? null : query.queryId)
                      }
                    }}
                  >
                    <div className="card-header">
                      <div className="company-info">
                        <h4>{query.companyName || "N/A"}</h4>
                        <span className="site-address">{query.siteAddress || "N/A"}</span>
                      </div>
                      <div className="card-actions">
                        <div className="query-status pending">{getStatusIcon("Pending")} Pending</div>
                        <div className={`category-badge badge-${category}`}>
                          {getCategoryIcon(query.category)}
                          <span>{query.category || "Cement"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="query-preview">
                      <p>{query.query?.substring(0, 100)}...</p>
                      <div className="query-timestamp">{query.displayTimestamp}</div>
                    </div>
                    {isSelected && (
                      <div className="query-details-expanded">
                        <div className="query-details">
                          {/* Category-specific details */}
                          {(category === "cement") && (
                            <>
                              <div className="detail-row">
                                <span>Brand:</span>
                                <span>{query.brand || "N/A"}</span>
                              </div>
                              <div className="detail-row">
                                <span>Grade:</span>
                                <span>{query.grade || "N/A"}</span>
                              </div>
                            </>
                          )}
                          {category === "steel" && (
                            <>
                              <div className="detail-row">
                                <span>Steel Type:</span>
                                <span>{query.steelType || "N/A"}</span>
                              </div>
                              <div className="detail-row">
                                <span>Required Quantity:</span>
                                <span>{query.requiredQuantity || "N/A"}</span>
                              </div>
                              <div className="detail-row">
                                <span>Steel Grade:</span>
                                <span>{query.steelGrade || query.steelType || "N/A"}</span>
                              </div>
                            </>
                          )}
                          {category === "machinery" && (
                            <div className="detail-row">
                              <span>Machinery Type:</span>
                              <span>{query.machineryType || "N/A"}</span>
                            </div>
                          )}
                          {category === "finance" && (
                            <>
                              <div className="detail-row">
                                <span>Finance Type:</span>
                                <span>{query.financeType || "N/A"}</span>
                              </div>
                              <div className="detail-row">
                                <span>Requirement Amount:</span>
                                <span>{query.requirementAmount || "N/A"}</span>
                              </div>
                            </>
                          )}
                          <div className="detail-row">
                            <span>Contact:</span>
                            <span>{query.contactNumber || "N/A"}</span>
                          </div>
                        </div>
                        <div className="query-content">
                          <h5>Customer Query:</h5>
                          <p>{query.query || "N/A"}</p>
                        </div>
                        <div className="reply-form">
                          <h5>Your Response</h5>
                          <div className="form-group">
                            <textarea
                              placeholder="Type your response here... *"
                              value={supplierReplies[query.queryId]?.responseMessage || ""}
                              onChange={(e) => handleInputChange(query.queryId, "responseMessage", e.target.value)}
                              maxLength={MAX_RESPONSE_LENGTH}
                              rows={4}
                              className="response-textarea"
                            />
                            <div className="character-count">
                              {supplierReplies[query.queryId]?.responseMessage?.length || 0}/{MAX_RESPONSE_LENGTH}{" "}
                              characters
                            </div>
                          </div>
                          <button
                            className="submit-button"
                            onClick={() => handleSendReply(query)}
                            disabled={!supplierReplies[query.queryId]?.responseMessage}
                          >
                            Send Response
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })
            ) : (
              <div className="no-queries">
                <MessageSquare size={48} />
                <p>No new queries found</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Responded Queries */}
        <div className="right-column">
          <div className="column-header">
            <CheckCircle size={18} />
            <h3>Responded Queries</h3>
            <span className="count-badge">{filteredSubmittedQueries.length}</span>
          </div>
          <div className="queries-list">
            {filteredSubmittedQueries.length > 0 ? (
              filteredSubmittedQueries.map((query) => {
                const category = query.category?.toLowerCase() || "cement"
                const isSelected = selectedRespondedQueryId === query.queryId
                return (
                  <div
                    key={query.queryId}
                    className={`query-card responded-query ${isSelected ? "expanded" : ""}`}
                    onClick={() => setSelectedRespondedQueryId(isSelected ? null : query.queryId)}
                  >
                    {/* Header */}
                    <div className="card-header">
                      <div className="company-info">
                        <h4>{query.companyName}</h4>
                        <span className="site-address">{query.siteAddress}</span>
                      </div>
                      <div className="card-actions">
                        <div className="query-status resolved">{getStatusIcon("Resolved")} Resolved</div>
                        <button
                          className={`delete-button ${deleteConfirm === query.queryId ? "confirm" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation() // prevent toggling
                            handleDeleteQuery(query)
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    {!isSelected ? (
                      <>
                        {/* Preview View */}
                        <div className="query-preview">
                          <p>{query.query?.substring(0, 80)}...</p>
                        </div>
                        <div className="response-preview">
                          <h5>Your Response:</h5>
                          <p>{query.supplierReply.responseMessage?.substring(0, 100)}...</p>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Full Details */}
                        <div className="query-details-expanded">
                          {/* Category-specific Details */}
                          {(category === "cement") && (
                            <>
                              <div className="detail-row">
                                <span>Brand:</span>
                                <span>{query.brand || "N/A"}</span>
                              </div>
                              <div className="detail-row">
                                <span>Grade:</span>
                                <span>{query.grade || "N/A"}</span>
                              </div>
                            </>
                          )}
                          {category === "steel" && (
                            <>
                              <div className="detail-row">
                                <span>Steel Type:</span>
                                <span>{query.steelType || "N/A"}</span>
                              </div>
                              <div className="detail-row">
                                <span>Required Quantity:</span>
                                <span>{query.requiredQuantity || "N/A"}</span>
                              </div>
                              <div className="detail-row">
                                <span>Steel Grade:</span>
                                <span>{query.steelGrade || query.steelType || "N/A"}</span>
                              </div>
                            </>
                          )}
                          {category === "machinery" && (
                            <div className="detail-row">
                              <span>Machinery Type:</span>
                              <span>{query.machineryType || "N/A"}</span>
                            </div>
                          )}
                          {category === "finance" && (
                            <>
                              <div className="detail-row">
                                <span>Finance Type:</span>
                                <span>{query.financeType || "N/A"}</span>
                              </div>
                              <div className="detail-row">
                                <span>Requirement Amount:</span>
                                <span>{query.requirementAmount || "N/A"}</span>
                              </div>
                            </>
                          )}
                          <div className="detail-row">
                            <span>Contact:</span>
                            <span>{query.contactNumber || "N/A"}</span>
                          </div>
                          <div className="query-content">
                            <h5>Customer Query:</h5>
                            <p>{query.query || "N/A"}</p>
                          </div>
                          <div className="response-preview">
                            <h5>Your Response:</h5>
                            <p>{query.supplierReply.responseMessage || "No response available."}</p>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="query-timestamp">
                      Responded: {new Date(query.supplierReply.submittedAt).toLocaleDateString()}
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="no-queries">
                <CheckCircle size={48} />
                <p>No responded queries found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserQueries
